import { AreaType } from 'interfaces/areaType';
import { ClassificationModel } from 'interfaces/classificationModel';
import { ClusterType } from 'interfaces/clusterType';
import { FeatureFormatter } from 'interfaces/featureFormatter';
import FeatureParam from 'interfaces/featureParams';
import { StainType } from 'interfaces/stainType';
import { ClusterIdReassignments } from 'interfaces/study';
import { TaxonomyTag } from 'interfaces/taxonomyTag';
import Visualization from 'interfaces/visualization';
import { useMemo } from 'react';
import { applyPanelRnaProbeDisplayNames, getPanelRnaProbeDisplayNames } from 'utils/multiplex/panelChannelPresentation';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';
import useTaxonomyTags from 'utils/queryHooks/taxonomy/useTaxonomyTags';
import {
  UiSettings,
  useAreaTypeOptions,
  useClassificationModelOptions,
  useClusterTypeOptions,
  useFeatureFormatters,
  useFeatureParams,
  useStainTypeOptions,
  useUiSettings,
  useVisualizationTypeOptions,
} from 'utils/queryHooks/uiConstantsHooks';
import useStudy from 'utils/useStudy';

export interface FormatBracketsOptions {
  stainTypeOptions: StainType[];
  areaTypeOptions: AreaType[];
  visualizationTypeOptions?: Visualization[];
  clusterTypeOptions: ClusterType[];
  classificationModelOptions: ClassificationModel[];
  addStain: boolean;
  uiSettings: UiSettings;
  featureFormatters: FeatureFormatter[];
  taxonomyIdToDisplayNameMap: Record<string, string>;
  taxonomyTags: TaxonomyTag[];
  featureParams: FeatureParam[];
  studyClusterIdReassignments: ClusterIdReassignments;
  rnaProbeDisplayNameOverridesByProbeId?: Record<string, string>;
  isLoading: boolean;
  orchestrationId: string;
}

const EMPTY_CLUSTER_ID_REASSIGNMENTS: ClusterIdReassignments = {};

export const useFormatBracketsOptions = ({
  addStain,
  studyId,
}: {
  addStain: boolean;
  studyId: string;
}): Omit<FormatBracketsOptions, 'orchestrationId'> => {
  const { isLoadingStainTypeOptions, stainTypeOptions } = useStainTypeOptions();
  const { isLoadingAreaTypes, areaTypeOptions } = useAreaTypeOptions();
  const { isLoadingClusterTypes, clusterTypeOptions } = useClusterTypeOptions();
  const { isLoadingVisualizationTypes, visualizationTypeOptions } = useVisualizationTypeOptions();
  const { isLoadingClassificationModels, classificationModelOptions } = useClassificationModelOptions();
  const { uiSettings, isLoadingUiSettings } = useUiSettings();
  const { featureFormatters, isLoadingFeatureFormatters } = useFeatureFormatters();
  const { featureParams, isLoadingFeatureParams } = useFeatureParams();

  const { isLoading: isLoadingTaxonomies, idToDisplayNameMap: taxonomyIdToDisplayNameMap } = useTaxonomy();
  const { isLoading: isLoadingTaxonomyTags, data: taxonomyTags } = useTaxonomyTags();

  const { isLoading: isLoadingStudy, study } = useStudy(studyId, { enabled: Boolean(studyId) });

  const studyClusterIdReassignments = study?.settings?.clusterIdReassignments || EMPTY_CLUSTER_ID_REASSIGNMENTS;
  const rnaProbeDisplayNameOverridesByProbeId = getPanelRnaProbeDisplayNames(study?.settings?.multiplexPanels);
  const overriddenStainTypeOptions = applyPanelRnaProbeDisplayNames(
    stainTypeOptions,
    rnaProbeDisplayNameOverridesByProbeId
  );

  return useMemo(
    () => ({
      addStain,
      rnaProbeDisplayNameOverridesByProbeId,
      stainTypeOptions: overriddenStainTypeOptions,
      areaTypeOptions,
      clusterTypeOptions,
      visualizationTypeOptions,
      classificationModelOptions,
      uiSettings,
      featureFormatters,
      taxonomyIdToDisplayNameMap,
      taxonomyTags,
      featureParams,
      studyClusterIdReassignments,
      isLoading:
        isLoadingStudy ||
        isLoadingStainTypeOptions ||
        isLoadingAreaTypes ||
        isLoadingClusterTypes ||
        isLoadingVisualizationTypes ||
        isLoadingClassificationModels ||
        isLoadingUiSettings ||
        isLoadingFeatureFormatters ||
        isLoadingTaxonomies ||
        isLoadingTaxonomyTags ||
        isLoadingFeatureParams,
    }),
    [
      addStain,
      rnaProbeDisplayNameOverridesByProbeId,
      overriddenStainTypeOptions,
      areaTypeOptions,
      clusterTypeOptions,
      visualizationTypeOptions,
      classificationModelOptions,
      uiSettings,
      featureFormatters,
      taxonomyIdToDisplayNameMap,
      taxonomyTags,
      featureParams,
      studyClusterIdReassignments,
      isLoadingStudy,
      isLoadingStainTypeOptions,
      isLoadingAreaTypes,
      isLoadingClusterTypes,
      isLoadingVisualizationTypes,
      isLoadingClassificationModels,
      isLoadingUiSettings,
      isLoadingFeatureFormatters,
      isLoadingTaxonomies,
      isLoadingTaxonomyTags,
      isLoadingFeatureParams,
    ]
  );
};
